import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useAssets } from "@/hooks/useAssets";
import { exportInventoryCSV } from "@/lib/csvExport";
import InsuranceExportSheet from "@/components/InsuranceExportSheet";
import { Loader2, Download, FileDown, Sparkles } from "lucide-react";
import { VAULT_PRICE_MONTHLY } from "@/lib/featureFlags";
import { useCheckout } from "@/hooks/useCheckout";

/**
 * Full-page overlay shown when a user's trial has expired (tier === "expired").
 *
 * Design intent: calm, non-aggressive. Not a dark-pattern upsell.
 * Tone: dry and understated. Give them an honest choice.
 */
export default function TrialExpiredScreen() {
  const { user, signOut } = useAuth();
  const { data: assets } = useAssets();
  const assetCount = assets?.length ?? 0;

  const { startCheckout, loading: checkoutLoading } = useCheckout();
  const [exportSheetOpen, setExportSheetOpen] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const navigate = useNavigate();

  // --- Sign out ---
  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  // --- CSV export ---
  const handleCsvExport = async () => {
    if (!user) return;
    setCsvLoading(true);
    try {
      await exportInventoryCSV(user.id);
      toast({ title: "Export ready", description: "Your inventory CSV has been downloaded." });
    } catch (err: any) {
      toast({
        title: "Export failed",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <>
      {/* Full-page overlay — sits above AppLayout content */}
      <div className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-sm flex items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-8 animate-fade-in">

          {/* Headline block */}
          <div className="space-y-2 text-center">
            <h1 className="font-display text-2xl font-bold text-foreground leading-snug">
              {assetCount === 0 ? (
                "Your trial is over. Your account is still here."
              ) : (
                <>
                  You documented{" "}
                  <span className="text-primary">{assetCount}</span>{" "}
                  {assetCount === 1 ? "thing" : "things"}.{" "}
                  Seems like a waste to lose them.
                </>
              )}
            </h1>
            <p className="text-sm text-muted-foreground">
              Your trial is over, but your inventory is still here. Keep it.
            </p>
          </div>

          {/* Vault benefits */}
          <ul className="space-y-3">
            {[
              {
                text: "Unlimited assets",
                sub: "your home contains more than 25 things",
              },
              {
                text: "AI-powered scanning and bulk add",
                sub: null,
              },
              {
                text: "Insurance-ready exports",
                sub: "whenever you need them",
              },
            ].map((benefit) => (
              <li key={benefit.text} className="flex items-start gap-3">
                <span className="mt-1 h-4 w-4 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary block" />
                </span>
                <span className="text-sm text-foreground">
                  {benefit.text}
                  {benefit.sub && (
                    <span className="text-muted-foreground"> — {benefit.sub}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>

          {/* Primary CTA */}
          <Button
            onClick={() => startCheckout({ plan: "monthly" })}
            disabled={checkoutLoading}
            className="w-full min-h-[52px] text-base font-semibold gap-2"
          >
            {checkoutLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {checkoutLoading ? "Redirecting…" : `Upgrade to Vault — ${VAULT_PRICE_MONTHLY}`}
          </Button>

          {/* Export section — muted, below the fold */}
          <div className="space-y-3 pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Not ready? Take your data with you first.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                onClick={() => setExportSheetOpen(true)}
                className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground min-h-[44px]"
              >
                <Download className="h-4 w-4 shrink-0" />
                Download Insurance Packet
              </Button>
              <Button
                variant="ghost"
                onClick={handleCsvExport}
                disabled={csvLoading}
                className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground min-h-[44px]"
              >
                {csvLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                ) : (
                  <FileDown className="h-4 w-4 shrink-0" />
                )}
                Export as CSV
              </Button>
            </div>
          </div>

          {/* Footer micro-copy */}
          <p className="text-center text-xs text-muted-foreground/60">
            No hard feelings. We'll be here.
          </p>

          {/* Sign-out escape hatch */}
          <p className="text-center pb-4">
            <button
              onClick={handleSignOut}
              className="text-xs text-muted-foreground/50 hover:text-muted-foreground underline underline-offset-2 transition-colors"
            >
              Sign out
            </button>
          </p>
        </div>
      </div>

      {/* Insurance Export Sheet */}
      <InsuranceExportSheet open={exportSheetOpen} onOpenChange={setExportSheetOpen} />
    </>
  );
}
